<template>
    <div id="employee-leave-page">
        <div class="top-buttons">
            <ui5-button @click="$router.push('/employeedetails/' + idParam)">
                {{ trans('pages.employee_leave.back') }}
            </ui5-button>
        </div>
        <div class="leftPane">
            <h2>{{ trans('pages.employee_leave.leave_days') }}</h2>
            <table class="leave-list">
                <thead>
                    <tr>
                        <th class="dateHeading">{{ trans('pages.employee_leave.date') }}</th>
                        <th class="startTimeHeading">{{ trans('pages.employee_leave.from') }}</th>
                        <th class="endTimeHeading">{{ trans('pages.employee_leave.to') }}</th>
                        <th class="actionHeading">{{ trans('pages.employee_leave.actions') }}</th>
                    </tr>
                </thead>
                <tbody></tbody>
                <tbody>
                    <tr v-if="items.length == 0">
                        <td colspan="4">
                            {{ trans('pages.employee_leave.no_leave_days', {year: selectedYear}) }}
                        </td>
                    </tr>
                    <template v-else v-for="(item, index) in items">
                        <tr
                            :key="'item.' + item.id"
                            :class="{ highlight: highlightedId == item.id }">
                            <td class="date">{{ item.day }}</td>
                            <td class="startTime">
                                {{ parseTime(item.startTime) }}
                            </td>
                            <td class="endTime">
                                {{ parseTime(item.endTime) }}
                            </td>
                            <td class="action">
                                <i
                                    class="fa fa-pencil"
                                    @click="setUpdateItem(item)"
                                    :alt="trans('pages.employee_leave.edit')"
                                    :title="trans('pages.employee_leave.edit')" />
                                <i
                                    class="fa fa-trash"
                                    @click="deleteLeave(item.id, index)"
                                    :alt="trans('pages.employee_leave.delete')"
                                    :title="trans('pages.employee_leave.delete')" />
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <div class="controls">
                <ui5-button
                    class="previousDate"
                    @click="shiftDateForLeaveList(-1)">
                    {{ trans('pages.employee_leave.previous') }}
                </ui5-button>
                <div>{{ trans('pages.employee_leave.leave_days_for', {year: selectedYear}) }}</div>
                <ui5-button class="nextDate" @click="shiftDateForLeaveList(+1)">
                    {{ trans('pages.employee_leave.next') }}
                </ui5-button>
            </div>
            <div id="addLeaveEmployee">
                <h3>{{ trans('pages.employee_leave.add_leave_days') }}</h3>
                <div class="field">
                    <ui5-label for="add-day" required>{{ trans('pages.employee_leave.date_from') }}</ui5-label>
                    <ui5-date-picker
                        id="add-day"
                        required
                        name="day"
                        ref="add-day"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="addItem.day = $event.detail.value"
                        :value="addItem.day" />
                    <div
                        class="error"
                        v-if="
                            showAddValidationErrors && getError('addItem.day')
                        ">
                        {{ getError('addItem.day') }}
                    </div>
                </div>
                <div class="field">
                    <ui5-label for="add-dateEnd">{{ trans('pages.employee_leave.date_to') }}</ui5-label>
                    <ui5-date-picker
                        id="add-dateEnd"
                        name="dateEnd"
                        ref="add-dateEnd"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="addItem.dateEnd = $event.detail.value"
                        :value="addItem.dateEnd" />
                    <div
                        class="error"
                        v-if="
                            showAddValidationErrors &&
                            getError('addItem.dateEnd')
                        ">
                        {{ getError('addItem.dateEnd') }}
                    </div>
                </div>
                <div class="field">
                    <ui5-label for="add-startTime" required>{{ trans('pages.employee_leave.from') }}</ui5-label>
                    <ui5-time-picker
                        :format-pattern="timePattern"
                        required
                        class="startTime"
                        id="add-startTime"
                        ref="add-startTime"
                        type="Text"
                        name="startTime"
                        :value="addItem.startTime"
                        @change="addItem.startTime = getTime($event.target)"
                        @blur="
                            addItem.startTime = getTime($refs['add-startTime'])
                        "
                        :placeholder="timePattern" />
                    <div
                        class="error"
                        v-if="
                            showAddValidationErrors &&
                            getError('addItem.startTime')
                        ">
                        {{ getError('addItem.startTime') }}
                    </div>
                </div>
                <div class="field">
                    <ui5-label for="add-endTime" required>{{ trans('pages.employee_leave.to') }}</ui5-label>
                    <ui5-time-picker
                        :format-pattern="timePattern"
                        required
                        class="endTime"
                        id="add-endTime"
                        ref="addEndTime"
                        type="Text"
                        name="endTime"
                        :value="addItem.endTime"
                        @change="addItem.endTime = getTime($event.target)"
                        @blur="addItem.endTime = getTime($refs['addEndTime'])"
                        :placeholder="timePattern" />
                    <div
                        class="error"
                        v-if="
                            showAddValidationErrors &&
                            getError('addItem.endTime')
                        ">
                        {{ getError('addItem.endTime') }}
                    </div>
                </div>
                <div class="field button-field">
                    <ui5-button
                        class="addLeaveEmployeeButton"
                        @click="addLeave"
                        id="addLeaveEmployeeButton"
                        name="addLeaveEmployeeButton">
                        {{ trans('pages.employee_leave.add') }}
                    </ui5-button>
                </div>
            </div>
        </div>
        <div class="rightPane">
            <div id="updateLeaveForm" v-if="updateItem && updateItem.id">
                <h3>{{ trans('pages.employee_leave.edit_leave_day') }}</h3>
                <input type="hidden" name="id" class="id" />
                <div class="field">
                    <ui5-label for="update-date" required>{{ trans('pages.employee_leave.date') }}</ui5-label>
                    <ui5-date-picker
                        id="update-date"
                        required
                        name="date"
                        ref="updateDate"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="updateItem.day = $event.detail.value"
                        :value="updateItem.day" />
                    <div class="error" v-if="getError('updateItem.day')">
                        {{ getError('updateItem.day') }}
                    </div>
                </div>
                <div class="field">
                    <ui5-label for="update-startTime" required>{{ trans('pages.employee_leave.from') }}</ui5-label>
                    <ui5-time-picker
                        :format-pattern="timePattern"
                        required
                        class="startTime"
                        id="update-startTime"
                        ref="updateStartTime"
                        type="Text"
                        name="startTime"
                        :value="updateItem.startTime"
                        @change="updateItem.startTime = getTime($event.target)"
                        @blur="
                            updateItem.startTime = getTime(
                                $refs['updateStartTime']
                            )
                        "
                        :placeholder="timePattern" />
                    <div class="error" v-if="getError('updateItem.startTime')">
                        {{ getError('updateItem.startTime') }}
                    </div>
                </div>
                <div class="field">
                    <ui5-label for="update-endTime" required>{{ trans('pages.employee_leave.to') }}</ui5-label>
                    <ui5-time-picker
                        :format-pattern="timePattern"
                        required
                        class="endTime"
                        id="update-endTime"
                        ref="updateEndTime"
                        type="Text"
                        name="endTime"
                        :value="updateItem.endTime"
                        @change="updateItem.endTime = getTime($event.target)"
                        @blur="
                            updateItem.endTime = getTime($refs['updateEndTime'])
                        "
                        :placeholder="timePattern" />
                    <div class="error" v-if="getError('updateItem.endTime')">
                        {{ getError('updateItem.endTime') }}
                    </div>
                </div>
                <div class="field button-field">
                    <ui5-button
                        @click="updateLeave"
                        name="updateLeave"
                        id="updateLeave">
                        {{ trans('pages.employee_leave.save') }}
                    </ui5-button>
                    <ui5-button
                        @click="cancelUpdateForm"
                        name="cancelUpdateLeave"
                        class="cancelUpdateLeave">
                        {{ trans('pages.employee_leave.cancel') }}
                    </ui5-button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import userApi from '../api/user';
import employeeApi from '../api/employee';
import { required } from 'vuelidate/lib/validators';
import ErrorHelper from '../helpers/ErrorHelper';
import IdParamMixin from '../mixins/id-param';
import localization from '../mixins/localization';
import {
    showConfirmDialog,
    showInfoMessage,
    showErrorMessage,
} from '../dialogs';

const emptyAddItem = {
    id: null,
    day: '',
    dateEnd: '',
    startTime: '',
    endTime: '',
};
const emptyUpdateItem = {
    id: null,
    day: '',
    startTime: '',
    endTime: '',
};
export default {
    mixins: [IdParamMixin(), localization],
    data() {
        return {
            selectedYear: 2015,
            items: [],
            updateItem: { ...emptyUpdateItem },
            addItem: { ...emptyAddItem },
            highlightedId: null,
            showAddValidationErrors: false,
        };
    },
    computed: {
        datePattern: () => 'yyyy-MM-dd',
        timePattern: () => 'HH:mm',
    },
    methods: {
        hasAnyError(keys) {
            return keys.some((x) => this.getError(x) != null);
        },
        getError(key) {
            const isAfter = (endElement, startElement) => {
                if (!endElement || !startElement) {
                    this.$nextTick(this.$forceUpdate); //Necessary for the edit form somehow
                    return false;
                }
                return startElement.dateValue < endElement.dateValue;
            };

            const isAfterOrSame = (endElement, startElement) => {
                if (!endElement || !startElement) {
                    return false;
                }
                return startElement.dateValue <= endElement.dateValue;
            };

            const hasValueStateError = (element) => {
                if (!element) {
                    return false;
                }
                return element.valueState == 'Error';
            };

            const addItem = this.addItem;
            const updateItem = this.updateItem;
            if (key == 'addItem.day') {
                if (!addItem.day || !addItem.day.length) {
                    return this.trans('pages.employee_leave.required');
                }
                if (hasValueStateError(this.$refs['add-day'])) {
                    return this.trans('pages.employee_leave.enter_valid_date');
                }
            } else if (key == 'addItem.dateEnd') {
                if (addItem.dateEnd && addItem.dateEnd.length) {
                    if (hasValueStateError(this.$refs['add-dateEnd'])) {
                        return this.trans('pages.employee_leave.enter_valid_date');
                    }
                    if (
                        !isAfterOrSame(
                            this.$refs['add-dateEnd'],
                            this.$refs['add-day']
                        )
                    ) {
                        return this.trans('pages.employee_leave.must_be_after_or_equal', {date: addItem.day});
                    }
                }
            } else if (key == 'addItem.startTime') {
                if (!addItem.startTime || !addItem.startTime.length) {
                    return this.trans('pages.employee_leave.required');
                }
                if (hasValueStateError(this.$refs['add-startTime'])) {
                    return this.trans('pages.employee_leave.enter_valid_time');
                }
            } else if (key == 'addItem.endTime') {
                if (!addItem.endTime || !addItem.endTime.length) {
                    return this.trans('pages.employee_leave.required');
                }
                if (hasValueStateError(this.$refs['addEndTime'])) {
                    return this.trans('pages.employee_leave.enter_valid_time');
                }
                if (addItem.startTime && addItem.startTime.length) {
                    if (
                        !isAfter(
                            this.$refs['addEndTime'],
                            this.$refs['add-startTime']
                        )
                    ) {
                        return this.trans('pages.employee_leave.must_be_after', {time: addItem.startTime});
                    }
                }
            } else if (key == 'updateItem.day') {
                if (!updateItem.day || !updateItem.day.length) {
                    return this.trans('pages.employee_leave.required');
                }
                if (hasValueStateError(this.$refs['updateDate'])) {
                    return this.trans('pages.employee_leave.enter_valid_date');
                }
            } else if (key == 'updateItem.startTime') {
                if (!updateItem.startTime || !updateItem.startTime.length) {
                    return this.trans('pages.employee_leave.required');
                }
                if (hasValueStateError(this.$refs['updateStartTime'])) {
                    return this.trans('pages.employee_leave.enter_valid_time');
                }
            } else if (key == 'updateItem.endTime') {
                if (!updateItem.endTime || !updateItem.endTime.length) {
                    return this.trans('pages.employee_leave.required');
                }
                if (hasValueStateError(this.$refs['updateEndTime'])) {
                    return this.trans('pages.employee_leave.enter_valid_time');
                }
                if (updateItem.startTime && updateItem.startTime.length) {
                    if (
                        !isAfter(
                            this.$refs['updateEndTime'],
                            this.$refs['updateStartTime']
                        )
                    ) {
                        return this.trans('pages.employee_leave.must_be_after', {time: updateItem.startTime});
                    }
                }
            }
            return null;
        },
        parseTime(value) {
            const parts = value.toString().split(':');
            return parts[0] + ':' + parts[1];
        },
        setUpdateItem(item) {
            const updateItem = { ...item };
            updateItem.startTime = this.parseTime(updateItem.startTime);
            updateItem.endTime = this.parseTime(updateItem.endTime);
            this.updateItem = updateItem;
        },
        getTime(element) {
            const date = element.dateValue;
            if (!date) {
                return element.value;
            }
            return (
                date.getHours().toString().padStart(2, '0') +
                ':' +
                date.getMinutes().toString().padStart(2, '0')
            );
        },
        shiftDateForLeaveList(direction) {
            this.selectedYear += direction;
            this.getLeave();
        },
        load() {
            userApi.fetchUserDetails(this.idParam).then((response) => {
                const employee = response.data.data;
                this.selectedYear = new Date().getFullYear();
                this.$store.commit(
                    'general/setPageTitle',
                    this.trans('pages.employee_leave.leave_days_for_employee', {name: employee.name, year: this.selectedYear})
                );
                this.getLeave();
            });
        },
        updateLeave() {
            const hasError = this.hasAnyError([
                'updateItem.day',
                'updateItem.startTime',
                'updateItem.endTime',
            ]);
            if (hasError) {
                return showErrorMessage(this.trans('pages.employee_leave.resolve_input_errors'));
            }

            const updateItem = this.updateItem;
            employeeApi
                .updateLeave(
                    updateItem.id,
                    updateItem.day,
                    updateItem.startTime,
                    updateItem.endTime,
                    this.idParam
                )
                .then(() => {
                    this.highlightedId = updateItem.id;
                    setTimeout(() => {
                        this.highlightedId = null;
                    }, 500);
                    this.updateItem = { ...emptyUpdateItem };
                    this.getLeave();
                    showInfoMessage(this.trans('pages.employee_leave.leave_day_modified'));
                })
                .catch(() => {
                    showErrorMessage(this.trans('pages.employee_leave.leave_day_not_modified'));
                });
        },
        cancelUpdateForm() {
            this.updateItem = emptyUpdateItem;
        },
        addLeave() {
            const addItem = this.addItem;
            this.showAddValidationErrors = true;

            const hasError = this.hasAnyError([
                'addItem.day',
                'addItem.dateEnd',
                'addItem.startTime',
                'addItem.endTime',
            ]);
            if (hasError) {
                return showErrorMessage(this.trans('pages.employee_leave.resolve_input_errors'));
            }
            employeeApi
                .addLeave(
                    addItem.day,
                    addItem.dateEnd,
                    addItem.startTime,
                    addItem.endTime,
                    this.idParam
                )
                .then(() => {
                    this.load();
                    this.addItem = { ...emptyAddItem };
                    this.showAddValidationErrors = false;
                })
                .catch(() => {
                    showErrorMessage(this.trans('pages.employee_leave.leave_day_not_saved'));
                });
        },
        deleteLeave(id, index) {
            showConfirmDialog({
                title: this.trans('pages.employee_leave.delete_leave_day'),
                content: this.trans('pages.employee_leave.confirm_delete'),
                confirm: () => {
                    employeeApi
                        .deleteLeave(id)
                        .then(() => {
                            this.$delete(this.items, index);
                            showInfoMessage(this.trans('pages.employee_leave.leave_day_deleted'));
                        })
                        .catch(() => {
                            showErrorMessage(this.trans('pages.employee_leave.leave_day_not_deleted'));
                        });
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans('pages.employee_leave.yes'),
                cancelButton: this.trans('pages.employee_leave.no'),
            });
        },
        getLeave() {
            employeeApi
                .fetchLeaveForYear(this.selectedYear, this.idParam)
                .then((response) => {
                    this.items = response.data.data;
                });
        },
    },
};
</script>
